// Chantier : Boilerplate lot 4 (PLAN/audits/audit-boilerplate/BACKLOG.md)
// Regle metier : -
// Decision : -
// Labels/styles du module chantiers, extraits de lib/labels.ts et lib/utils.ts
// (decontamination du socle, Axe 1).

import type { PhaseChantier, StatutChantier } from '@/services/chantiers';
import { formatFullName } from '@/lib/utils';

export const STATUT_LABELS: Record<StatutChantier, string> = {
  en_cours_chiffrage: 'En cours de chiffrage',
  chiffre: 'Chiffré',
  brouillon: 'Brouillon',
  // RC3.03 (D-UX-PILOTAGE-ADMIN) : libelles demandes par SERME, admin et client.
  en_attente_traitement: 'Demande envoyée',
  en_cours: 'En cours de traitement',
  clos: 'Clos',
  annule: 'Annulé',
};

// `dot` : pastille des grilles admin (/operations), meme couleur que le badge.
export const STATUT_CHANTIER_STYLE: Record<StatutChantier, { badge: string; border: string; dot: string }> = {
  en_cours_chiffrage:    { badge: 'bg-orange-400 hover:bg-orange-500',         border: 'border-l-orange-400', dot: 'bg-orange-400' },
  chiffre:               { badge: 'bg-green-600 hover:bg-green-700',           border: 'border-l-green-600',  dot: 'bg-green-600'  },
  brouillon:             { badge: 'bg-gray-400 hover:bg-gray-500 text-white',  border: 'border-l-gray-400',   dot: 'bg-gray-400'   },
  en_attente_traitement: { badge: 'bg-amber-500 hover:bg-amber-600 text-white',border: 'border-l-amber-500',  dot: 'bg-amber-500'  },
  en_cours:              { badge: 'bg-blue-600 hover:bg-blue-700 text-white',  border: 'border-l-blue-600',   dot: 'bg-blue-600'   },
  clos:                  { badge: 'bg-gray-600 hover:bg-gray-700 text-white',  border: 'border-l-gray-600',   dot: 'bg-gray-600'   },
  annule:                { badge: 'bg-red-600 hover:bg-red-700',               border: 'border-l-red-600',    dot: 'bg-red-600'    },
};

export function statutLabel(s: string): string {
  return STATUT_LABELS[s as StatutChantier] || s;
}

/**
 * Chantier : - (revue client RC3.71 / RC3.72, Lot 11)
 * Regle metier : CH-R12 (dechet ajoute par le client)
 * Decision : D-UX-PILOTAGE-ADMIN, D-CAP-AJOUT-SIGNALE
 * Colonne « Dechets prets » de la grille Chantiers de /operations : texte affiche (filtre, export) et rang de
 * tri (urgence decroissante). Compte sur les dechets non annules, actifs ou non.
 */
export function etatDechetsPrets(c: {
  phase: PhaseChantier;
  statut: StatutChantier;
  nbDechetsNonAnnules: number;
  nbDechetsPrets: number;
  nbDechetsAjoutes: number;
}): { texte: string; urgence: number } {
  // « - » : rien a preparer (chiffrage, aucun dechet) ou chantier termine (clos, annule : lecture seule, CH-R21).
  if (c.statut === 'clos' || c.statut === 'annule' || c.phase === 'chiffrage' || c.nbDechetsNonAnnules === 0) {
    return { texte: '-', urgence: 0 };
  }
  if (c.statut === 'en_cours' && c.nbDechetsAjoutes > 0) {
    const n = c.nbDechetsAjoutes;
    return { texte: `Non · ${n} ajouté${n > 1 ? 's' : ''}`, urgence: 2 + n };
  }
  if (c.nbDechetsPrets === c.nbDechetsNonAnnules) return { texte: 'Oui', urgence: 1 };
  return { texte: 'Non', urgence: 2 };
}

// Formate le nom du producteur pour les PDF.
// Professionnel : retourne la raison sociale (nom de l'entreprise).
// Particulier : "Prenom NOM (Particulier)" (nom en majuscules, mention explicite).
// Retourne '' si nom et prenom sont vides.
export function formatProducteurNom(
  type: 'particulier' | 'professionnel' | null | undefined,
  nom: string | null | undefined,
  prenom: string | null | undefined
): string {
  if (type === 'particulier') {
    if (!nom && !prenom) return '';
    const nomMaj = (nom ?? '').toUpperCase();
    return `${formatFullName(prenom, nomMaj)} (Particulier)`;
  }
  return nom ?? '';
}
